import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';

const OrbitingProduct = ({ product, index, total, scrollYProgress, isMobile }) => {
  const navigate = useNavigate();

  const x = useTransform(scrollYProgress, (p) => {
    const maxRadius = isMobile ? 165 : 300;
    const r = p < 0.4 ? Math.sin((p / 0.4) * (Math.PI / 2)) * maxRadius : maxRadius;
    const baseAngle = (index * 2 * Math.PI) / total;
    const currentAngle = baseAngle + (p * Math.PI * 1.5);
    return r * Math.cos(currentAngle);
  });

  const y = useTransform(scrollYProgress, (p) => {
    const maxRadius = isMobile ? 165 : 300;
    const r = p < 0.4 ? Math.sin((p / 0.4) * (Math.PI / 2)) * maxRadius : maxRadius;
    const baseAngle = (index * 2 * Math.PI) / total;
    const currentAngle = baseAngle + (p * Math.PI * 1.5);
    return r * Math.sin(currentAngle);
  });

  return (
    <motion.div
      onClick={() => navigate(`/product/${product.id}`)}
      className="absolute w-20 h-28 sm:w-28 sm:h-36 md:w-44 md:h-56 rounded-2xl overflow-hidden shadow-2xl bg-white/80 backdrop-blur-sm border-2 border-white cursor-pointer group z-30 flex flex-col items-center justify-between p-2 md:p-3"
      style={{ x, y }}
      whileHover={{ scale: 1.05 }}
    >
      <img 
        src={product.image} 
        alt={product.name} 
        className="h-[55%] md:h-[60%] object-contain drop-shadow-xl group-hover:scale-110 group-hover:-translate-y-2 transition-transform duration-500" 
      />
      <div className="text-center w-full mt-1 md:mt-0">
        <h3 className="font-serif text-[8px] sm:text-[10px] md:text-xs text-brand-darkSage font-bold leading-tight line-clamp-2">
          {product.name}
        </h3>
        <p className="text-brand-lightText text-[6px] sm:text-[8px] md:text-[10px] uppercase tracking-wider mt-0.5 md:mt-1">
          {product.category}
        </p>
      </div>
    </motion.div>
  );
};

const Collection = () => {
  const { products } = useStore();
  const containerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const opacityText = useTransform(scrollYProgress, [0.1, 0.3], [0, 1]);
  const scaleText = useTransform(scrollYProgress, [0.1, 0.3], [0.8, 1]);
  const ring1Opacity = useTransform(scrollYProgress, [0.05, 0.25], [0, 1]);
  const ring2Opacity = useTransform(scrollYProgress, [0.15, 0.35], [0, 1]);

  const displayProducts = products.slice(0, 6);

  return (
    <section ref={containerRef} id="shop" className="h-[250vh] bg-brand-sand relative z-40">
      <div className="sticky top-0 h-screen w-full flex items-center justify-center">
        <div className="relative flex items-center justify-center">
          {/* Outer Ring */}
          <motion.div 
            className="absolute rounded-full border border-brand-darkSage/10 z-[100] pointer-events-none"
            style={{ width: isMobile ? '380px' : '750px', height: isMobile ? '380px' : '750px', opacity: ring2Opacity }}
          />
          
          {/* Inner Ring */}
          <motion.div 
            className="absolute rounded-full border border-brand-darkSage/20 z-0 pointer-events-none"
            style={{ width: isMobile ? '280px' : '550px', height: isMobile ? '280px' : '550px', opacity: ring1Opacity }}
          />

          {/* Core Center Area */}
          <div className="w-[190px] h-[190px] sm:w-[220px] sm:h-[220px] md:w-[380px] md:h-[380px] rounded-full border border-white/50 bg-white/30 backdrop-blur-md shadow-[0_0_60px_rgba(0,0,0,0.05)] flex items-center justify-center relative z-10">
            <motion.div 
              className="flex flex-col items-center justify-center relative z-20 text-center px-4 w-full"
              style={{ opacity: opacityText, scale: scaleText }}
            >
              <h2 className="text-base sm:text-xl md:text-4xl font-serif text-brand-darkSage leading-tight px-2">
                THE CEYLANTHRO<br />COLLECTION
              </h2>
            </motion.div>
          </div>

          {/* Orbiting Products */}
          {displayProducts.map((product, index) => (
            <OrbitingProduct 
              key={product.id} 
              product={product} 
              index={index} 
              total={displayProducts.length} 
              scrollYProgress={scrollYProgress}
              isMobile={isMobile}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Collection;